import { Typography, styled, Button, Avatar } from '@ori-ui/mui';
import type { GlobalCustomerRegistrationsRegistrationSponsorInfoDto } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { ReactElement, FC } from 'react';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useStepperData, useVirtualPageAnalyticsData } from '../../../contexts';
import { TranslationKey } from '../../../models';
import { stringAvatar } from '../../../utils';

interface ISponsorBoxProps {
  sponsorDetailInfo: GlobalCustomerRegistrationsRegistrationSponsorInfoDto;
  title: ReactElement;
  isVisibleNextButton: boolean;
  sponsorImage?: string;
}

const RootContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: '120px',
  height: '120px',
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
  textDecoration: 'none',
}));

const StyledNameLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  fontWeight: 'bold',
}));

const NexButtton = styled(Button)(({ theme }) => ({
  minWidth: '330px',
  marginTop: theme.spacing(8),
}));

export const SponsorBox: FC<ISponsorBoxProps> = (props) => {
  const { sponsorDetailInfo, title, isVisibleNextButton, sponsorImage } = props;
  const { consultantNumber, fullName } = sponsorDetailInfo;

  const {
    context: { stepperData },
  } = useStepperData();
  const { activeStep, setActiveStep, stepsOrder } = stepperData;
  const {
    virtualPageAnalyticsContext: { virtualPageAnalyticsData },
  } = useVirtualPageAnalyticsData();
  const { setPreviousStep } = virtualPageAnalyticsData;

  const { getTestIdProps } = useTestId();

  const handleNextStep = () => {
    setPreviousStep(stepsOrder[activeStep]);
    setActiveStep(activeStep + 1);
  };

  return (
    <RootContainer {...getTestIdProps('sponsorBox-sponsorForm')}>
      <StyledTitle
        variant="subtitle1"
        color="textPrimary"
      >
        {title}
      </StyledTitle>
      {fullName ? (
        <StyledAvatar
          {...stringAvatar(fullName)}
          src={sponsorImage}
        />
      ) : null}
      <StyledNameLabel
        variant="body1"
        color="textPrimary"
        align="center"
        sx={{ fontWeight: 'bold' }}
      >
        {fullName}
      </StyledNameLabel>
      <Typography
        variant="body1"
        color="textPrimary"
        align="center"
      >
        {consultantNumber}
      </Typography>

      {isVisibleNextButton ? (
        <NexButtton
          variant="contained"
          color="primary"
          onClick={() => handleNextStep()}
          {...getTestIdProps('sponsorBox-next-step-btn')}
        >
          <Localization
            id={TranslationKey.SecondStep_Button}
            defaultMessage={defaultTranslations.SecondStep_Button}
          />
        </NexButtton>
      ) : null}
    </RootContainer>
  );
};
